import { useNavigate } from 'react-router-dom';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@crestrewards/ui';
import { KeyRound, LogOut, Menu } from 'lucide-react';

import useAuth from '../hooks/useAuth';

/** Initials for the avatar, e.g. "Raj Singh" -> "RS", "raj@..." -> "R". */
function initialsFor(user) {
  const source = user?.name || user?.email || '';
  const parts = source.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

export default function Topbar({ onOpenNav }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  async function handleLogout() {
    await logout();
    navigate('/login', { replace: true });
  }

  return (
    <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center justify-between gap-4 border-b-hairline border-border-chrome bg-card px-4 sm:px-6">
      <div className="flex min-w-0 items-center gap-3">
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden"
          onClick={onOpenNav}
          aria-label="Open navigation"
        >
          <Menu />
        </Button>
        <p className="truncate text-meta text-muted-foreground">
          Signed in as{' '}
          <span className="font-medium text-foreground">{user?.email ?? 'unknown user'}</span>
        </p>
      </div>

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="rounded-full"
            aria-label="Account menu"
          >
            <span className="grid h-7 w-7 place-items-center rounded-full bg-primary-tint text-label font-semibold text-primary-on-tint">
              {initialsFor(user)}
            </span>
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent align="end" className="w-56">
          <DropdownMenuLabel className="font-normal">
            <span className="block truncate text-meta font-medium text-foreground">
              {user?.name ?? 'Super admin'}
            </span>
            <span className="block truncate text-label text-muted-foreground">
              {user?.email ?? '—'}
            </span>
          </DropdownMenuLabel>
          <DropdownMenuSeparator />
          {/* A navigation, not an import. The change-password form lives in
              features/auth/AccountPage.jsx — putting it in a dialog here would
              mean shared/ -> features/, and CLAUDE.md allows exactly one of
              those (useAuth) and says not to add another. */}
          <DropdownMenuItem onSelect={() => navigate('/account')}>
            <KeyRound aria-hidden="true" />
            Your account
          </DropdownMenuItem>
          <DropdownMenuItem onSelect={handleLogout}>
            <LogOut aria-hidden="true" />
            Sign out
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );
}
